import { useMemo } from "react";

import { isWeb, useV2GetBundleBySlugQuery } from "@stepapp/core";
import { useRouter } from "expo-router";
import { useAppSelector } from "./redux";

const webSegments = ["checklist", "configure", "date-and-address", "summary", "confirmation"];

export const useServiceNavigation = (slug?: string) => {
  const { replace } = useRouter();
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const properties = useAppSelector(({ user }) => user.properties);
  const { data, loading } = useV2GetBundleBySlugQuery({
    variables: { localizationId, slug: slug || "" },
    skip: !(slug && localizationId),
    fetchPolicy: "cache-and-network",
    onError: () => replace("/"),
  });

  const hasProperties =
    useMemo(
      () => properties.filter((item) => item.localizationId === localizationId),
      [properties, localizationId],
    )?.length > 0;

  const mobileSegments = useMemo(() => {
    const hasSubscriptionOptions =
      data?.v2GetBundleBySlug?.subscriptionOptions &&
      data?.v2GetBundleBySlug?.subscriptionOptions?.length > 0;
    const attributes = data?.v2GetBundleBySlug?.questions
      .concat()
      .sort((a, b) => a.position - b.position);
    const categories = data?.v2GetBundleBySlug?.categories
      .concat()
      .sort((a, b) => a.position - b.position);
    const attributeCount = attributes?.length || 0;
    const categoriesCount = categories?.length || 0;
    const hasServices =
      data?.v2GetBundleBySlug?.services && data?.v2GetBundleBySlug?.services?.length > 0;
    const questionScreens: string[] = [];

    const hasChecklist = (data?.v2GetBundleBySlug?.checkList?.length || 0) > 0;

    if (hasChecklist) {
      questionScreens.push("checklist");
    }

    if (hasProperties) {
      questionScreens.push("pick-property");
    }

    if (hasSubscriptionOptions) {
      questionScreens.push("question_subscription");
    }

    questionScreens.push(
      ...Array.from({ length: categoriesCount }, (_, i) => `question_${categories?.[i]?.id}`),
    );
    questionScreens.push(
      ...Array.from({ length: attributeCount }, (_, i) => `question_${attributes?.[i]?.id}`),
    );

    if (hasServices) {
      questionScreens.push("question_services");
    }

    const defaultScreens = [...questionScreens, "notes", "date"];

    if (!hasProperties) {
      defaultScreens.push("add-address");
    }

    return [...defaultScreens, "summary", "confirmation"];
  }, [
    data?.v2GetBundleBySlug?.categories,
    data?.v2GetBundleBySlug?.questions,
    data?.v2GetBundleBySlug?.services,
    data?.v2GetBundleBySlug?.subscriptionOptions,
    data?.v2GetBundleBySlug?.checkList,
    hasProperties,
  ]);

  return { loading, platformSegments: isWeb ? webSegments : mobileSegments };
};
